import { CanceledError, type AxiosError, type AxiosResponse, type InternalAxiosRequestConfig } from "axios";

type RuntimeSource = {
  token(): string;
  refresh(): Promise<string>;
  authState(state: "unavailable" | "forbidden" | "session-expired"): void;
  signal: AbortSignal;
};
type RetriableConfig = InternalAxiosRequestConfig & {
  _authRetried?: boolean;
  _runtimeBound?: boolean;
  _runtimeSource?: () => RuntimeSource | null;
};

export function bindRuntimeSource<T extends InternalAxiosRequestConfig>(config: T, runtime: RuntimeSource | null): T {
  const bound = config as T & RetriableConfig;
  bound._runtimeBound = true;
  // Axios deep-merges plain-object config values during client.request(config).
  // Keep this as a function so the request keeps a stable source reference.
  bound._runtimeSource = () => runtime;
  if (runtime) {
    const currentSignal = bound.signal as AbortSignal | undefined;
    bound.signal = currentSignal ? AbortSignal.any([currentSignal, runtime.signal]) : runtime.signal;
  }
  return config;
}

function waitForRefresh<T>(refresh: Promise<T>, signal: AbortSignal | undefined, config: RetriableConfig): Promise<T> {
  if (!signal) return refresh;
  if (signal.aborted) return Promise.reject(new CanceledError(undefined, config));
  return new Promise<T>((resolve, reject) => {
    const cleanup = () => signal.removeEventListener("abort", cancel);
    const cancel = () => { cleanup(); reject(new CanceledError(undefined, config)); };
    signal.addEventListener("abort", cancel, { once: true });
    refresh.then(
      (value) => { cleanup(); if (signal.aborted) reject(new CanceledError(undefined, config)); else resolve(value); },
      (failure: unknown) => { cleanup(); if (signal.aborted) reject(new CanceledError(undefined, config)); else reject(failure); },
    );
  });
}

export function applyBearer<T extends InternalAxiosRequestConfig>(config: T, token: string | null): T {
  if (token) config.headers.set("Authorization", `Bearer ${token}`);
  config.withCredentials = false; // header-only auth (spec 2026-10-15 §3): never cookies
  return config;
}

export interface AuthErrorDeps {
  isKeycloak: () => boolean;
  renew: () => Promise<boolean>;
  token: () => Promise<string | null>;
  retry: (config: InternalAxiosRequestConfig) => Promise<AxiosResponse>;
  signIn: () => void | Promise<void>;
  legacyLogin: () => void | Promise<void>;
  runtime?: () => RuntimeSource | null;
}

// What a failed API call does. Keycloak mode: on a 401, one shared renewal and
// one retry with the fresh token, else sign in again (loop-guarded). Legacy
// mode: a 401 starts the Cognito sign-in, as before. /health is never retried.
export function makeAuthErrorHandler(d: AuthErrorDeps) {
  return async (error: unknown): Promise<AxiosResponse> => {
    const e = error as AxiosError;
    const config = e?.config as RetriableConfig | undefined;
    const currentRuntime = d.runtime?.() ?? null;
    const requestWasBound = config?._runtimeBound === true;
    const runtime = requestWasBound ? config?._runtimeSource?.() ?? null : currentRuntime;
    if (config?.signal?.aborted) throw new CanceledError(undefined, config);
    // A late response belongs to the context that sent it. Do not use it to
    // renew or close a replacement session after a revision change/disposal.
    if (requestWasBound && runtime !== currentRuntime) throw error;
    if (runtime?.signal.aborted) throw new CanceledError(undefined, config);
    // A 403 on a native action is not evidence that the shell identity expired
    // or that the user's page grant was revoked; leave the runtime in place.
    // Bootstrap route authorization is checked against /api/me before render.
    if (runtime && e?.response?.status === 403) throw error;
    if (e?.response?.status !== 401 || config?.url?.includes("/health")) throw error;
    if (runtime) {
      if (!config || config._authRetried) {
        runtime.authState("unavailable");
        throw error;
      }
      try {
        await waitForRefresh(runtime.refresh(), config.signal as AbortSignal | undefined, config);
        if (config.signal?.aborted || runtime.signal.aborted) throw new CanceledError(undefined, config);
        if (d.runtime?.() !== runtime) throw error;
        config._authRetried = true;
        return d.retry(applyBearer(config, runtime.token()));
      } catch (failure) {
        if (config._authRetried) throw failure;
        if (config.signal?.aborted || runtime.signal.aborted) throw new CanceledError(undefined, config);
        if (d.runtime?.() !== runtime) throw error;
        const terminal = typeof failure === "object" && failure !== null && "kind" in failure && (failure as { kind?: unknown }).kind === "session-expired";
        runtime.authState(terminal ? "session-expired" : "unavailable");
        throw error;
      }
    }
    if (!d.isKeycloak()) {
      await d.legacyLogin();
      throw error;
    }
    if (config && !config._authRetried && (await d.renew())) {
      const token = await d.token();
      if (token) {
        config._authRetried = true;
        // The stale Authorization must be overwritten on the retried request.
        return d.retry(applyBearer(config, token));
      }
    }
    await d.signIn();
    throw error;
  };
}
